import { logger } from '@/lib/logger';
import { driverJsConfig } from '@src/const/driverJsConfig';
import { useAuthStore } from '@src/stores/authStore';
import { useToastStore } from '@src/stores/toastStore';
import { tourMutations } from '@therame/api-client';
import { Role, TourStatusFullSizeDto, TourType } from '@therame/shared';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { defineStore } from 'pinia';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import type { Router } from 'vue-router';
import { useExerciseLibraryCoreStore } from './exerciseLibraryCoreTourStore';
import { useOfficeMgmTourStore } from './officeMgmTourStore';
import { useOfficeProfileSettingsTourStore } from './officeProfileSettingsTourStore';
import { useOfficeShopTourStore } from './officeShopTourStore';

export type Tour = {
  title: string;
  tourType: TourType | undefined;
  tourSteps: DriveStep[];
  route: string;
  role: Role[];
};

type TourStore = {
  useListenToClick: (
    elementId: string,
    driverObj?: Driver,
    moveToStepIndex?: number,
  ) => Promise<void>;
  showStartTour: Ref<boolean>;
  dismissTour: () => void;
  initializeTour: (
    selectedTour: TourType | undefined,
    router: Router,
  ) => Promise<Driver | undefined>;
  driverObject: Ref<Driver | undefined>;
  tours: Tour[];
  currentTour: Ref<Tour | undefined>;
  setTourByType: (newTourType: TourType | undefined) => void;
  getTourByType: (tourType: TourType | undefined) => Tour | undefined;
  setTourToComplete: () => Promise<void>;
  tourStatus: Ref<TourStatusFullSizeDto | undefined>;
  mode: string | undefined;
  startTourTilesEnabled: Ref<boolean>;
  isAllowedToStartTour: (tourType: TourType | undefined, userRole?: Role) => boolean;
  initializeFloatingVideoContainer: (tourType: TourType) => void;
  allowedToursForMyRole: ComputedRef<Tour[]>;
};

export const useTourStore = defineStore('tour', (): TourStore => {
  const toastStore = useToastStore();
  const authStore = useAuthStore();

  const mode = import.meta.env.VITE_MODE;

  /**
   * is true in the first 30 days after the user has signed up
   */
  const startTourTilesEnabled = ref<boolean>(false);
  const showStartTour = ref<boolean>(false);
  const currentTour = ref<Tour | undefined>(undefined);
  const driverObject = ref<Driver>();
  const tourStatus = ref<TourStatusFullSizeDto | undefined>(undefined);
  const activeTourClickListeners = ref<Array<{ element: Element; handler: EventListener }>>([]);

  // steps stores where each DriverStep array is stored
  const exerciseLibraryCoreTourStore = useExerciseLibraryCoreStore();
  const officeMgmTourStore = useOfficeMgmTourStore();
  const officeProfileSettingsTourStore = useOfficeProfileSettingsTourStore();
  const officeShopTourStore = useOfficeShopTourStore();

  const tours = [
    {
      title: 'Übungsbibliothek - Wie du deine Patienten motivieren kannst!',
      tourType: TourType.EXERCISE_LIBRARY_TOUR,
      tourSteps: exerciseLibraryCoreTourStore.exerciseLibraryCoreTourSteps,
      route: '/exercises/library',
      role: [Role.THERAPIST, Role.ADMIN],
    },
    {
      title: 'Praxisverwaltung - In zwei Klicks Personal einladen!',
      tourType: TourType.OFFICE_MGM_TOUR,
      tourSteps: officeMgmTourStore.officeMgmTourSteps,
      route: '/office/staff',
      role: [Role.ADMIN],
    },
    {
      title: 'Praxisverwaltung - Zeit sparen beim Onboarding deiner Patienten!',
      tourType: TourType.OFFICE_PROFILE_SETTINGS_TOUR,
      tourSteps: officeProfileSettingsTourStore.officeProfileSettingsTourSteps,
      route: '/office/subscriptions',
      role: [Role.ADMIN],
    },
    {
      title: 'Shop Verwaltung',
      tourType: TourType.OFFICE_SHOP_TOUR,
      tourSteps: officeShopTourStore.officeShopTourSteps,
      route: '/office-shop',
      role: [Role.ADMIN],
    },
    /* {
      title: 'Videosprechstunde - Behandlungsausfälle minimieren!',
      tourType: TourType.VIDEO_CALL_TOUR,
      tourSteps: videoCallTourStore.videoCallTourSteps,
      route: '/call/waiting-room',
      role: [Role.ADMIN, Role.THERAPIST],
    }, */
  ];

  const allowedToursForMyRole = computed(() => {
    const userRole = authStore.authContext?.user?.role;
    if (!userRole) {
      return [];
    }
    return tours.filter((tour) => tour.role.includes(userRole));
  });

  const isAllowedToStartTour = (tourType: TourType | undefined, userRole?: Role) => {
    const tour = getTourByType(tourType);
    if (!tour) {
      return false;
    }

    return userRole ? tour.role.includes(userRole) : false;
  };

  const getTourByType = (tourType: TourType | undefined) => {
    return tours.find((tour) => tour.tourType === tourType);
  };

  const setTourByType = (newTourType: TourType | undefined) => {
    currentTour.value = getTourByType(newTourType);
  };

  const hasStartedTourType = (tourType: TourType | undefined) => {
    switch (tourType) {
      case TourType.EXERCISE_LIBRARY_TOUR:
        return !!tourStatus.value?.exerciseLibraryTourCompleted;
      case TourType.OFFICE_MGM_TOUR:
        return !!tourStatus.value?.officeMgmTourCompleted;
      case TourType.OFFICE_PROFILE_SETTINGS_TOUR:
        return !!tourStatus.value?.officeProfileSettingsTourCompleted;
      case TourType.OFFICE_SHOP_TOUR:
        return !!tourStatus.value?.officeShopTourCompleted;
      case TourType.VIDEO_CALL_TOUR:
        return !!tourStatus.value?.videoCallTourCompleted;
      default:
        return false;
    }
  };

  const initializeFloatingVideoContainer = (tourType: TourType) => {
    if (import.meta.env.VITE_MODE && import.meta.env.VITE_MODE.startsWith('TEST')) {
      return;
    }

    // No tours in challenge mode — the challenge panel drives its own guided flow.
    if (window.localStorage.getItem('challenge_active') === 'true') {
      return;
    }

    if (!isAllowedToStartTour(tourType, authStore.authContext?.user?.role)) {
      return;
    }
    setTourByType(tourType);
    showStartTour.value = startTourTilesEnabled.value && !hasStartedTourType(tourType);
  };

  /**
   * Purpose is to listen to click events during the driver.js tour to allow the user to move forward
   * @param {string} elementId the elements unique id, used to locate the element in the DOM
   * @param {Driver} driverObj driver object, needed to move forward/ backwards in the tour
   * @param {number} moveToStepIndex index of the step to move to after click has been completed
   */
  const useListenToClick = async (
    elementId: string,
    driverObj?: Driver,
    moveToStepIndex?: number,
  ) => {
    const clearTourClickListeners = () => {
      activeTourClickListeners.value.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      activeTourClickListeners.value = [];
    };

    const waitForElement = async (
      selector: string,
      maxRetries = 20,
      delayMs = 100,
    ): Promise<Element | null> => {
      for (let i = 0; i < maxRetries; i++) {
        const element = document.querySelector(selector);
        if (element) {
          return element;
        }

        await new Promise((res) => setTimeout(res, delayMs));
      }

      return null;
    };

    clearTourClickListeners();

    const clickElement = await waitForElement(elementId);
    if (!clickElement) {
      return;
    }

    const listener: EventListener = () => {
      // Ignore stale listeners if the tour has already been closed.
      if (!document.querySelector('.driver-popover')) {
        return;
      }

      if (moveToStepIndex) {
        setTimeout(() => {
          driverObj?.moveTo(moveToStepIndex);
        }, 250);
      }
    };

    clickElement.addEventListener('click', listener, { once: true });
    activeTourClickListeners.value.push({ element: clickElement, handler: listener });
  };

  const waitForFirstDriverElement = async (
    step: DriveStep,
    maxRetries = 20,
    delayMs = 200,
    postDelayMs = 700,
  ): Promise<boolean> => {
    for (let i = 0; i < maxRetries; i++) {
      const el =
        typeof step.element === 'string' ? document.querySelector(step.element) : step.element;

      if (el) {
        if (postDelayMs > 0) {
          await new Promise((res) => setTimeout(res, postDelayMs));
        }
        return true;
      }

      await new Promise((res) => setTimeout(res, delayMs));
    }

    return false;
  };

  /**
   * Starts the appropriate tour by fetching steps from calculateTourSteps() and creating a driver object
   * @returns `driverObject: Driver`
   */
  const initializeTour = async (selectedTour: TourType | undefined, router: Router) => {
    currentTour.value = getTourByType(selectedTour);

    showStartTour.value = false;
    if (!currentTour.value?.tourSteps || currentTour.value?.tourSteps.length === 0) {
      return;
    }

    // Navigate to tour location
    const currentRoute = router.currentRoute.value.path;
    const targetRoute = currentTour.value?.route;

    if (currentRoute !== targetRoute) {
      router.push(targetRoute);
    }

    // Wait for the first step element to be ready
    const firstStep = currentTour.value.tourSteps[0];
    const elementReady = await waitForFirstDriverElement(firstStep);

    if (!elementReady) {
      logger.warn({ msg: 'First tour step element not found in time.' });
      return;
    }

    // Initialize the driver object with the tour steps
    driverObject.value = driver(
      driverJsConfig({
        steps: currentTour.value?.tourSteps,
        onDestroyed: () => {
          activeTourClickListeners.value.forEach(({ element, handler }) => {
            element.removeEventListener('click', handler);
          });
          activeTourClickListeners.value = [];

          showStartTour.value = false;

          // Prevent the same tour from being offered again immediately after manual close.
          setTourToComplete().catch((error) => {
            logger.warn({ msg: 'Failed to mark tour as complete on destroy.', error });
          });
        },
      }),
    );

    driverObject.value?.drive();
    return driverObject.value;
  };

  /**
   * determines if the `<StartTourTile/>` component should be present in the DOM
   */
  const dismissTour = () => {
    showStartTour.value = false;
    setTourToComplete();
    toastStore.addToast({
      type: 'info',
      text: 'Du kannst die Tour jederzeit unter "Hilfe" erneut starten.',
    });
  };

  const setTourToComplete = async () => {
    if (currentTour.value) {
      tourStatus.value = await tourMutations.complete({
        tourType: currentTour.value.tourType!,
      });
    }
  };

  const store: TourStore = {
    useListenToClick,
    showStartTour,
    dismissTour,
    initializeTour,
    driverObject,
    tours,
    currentTour,
    setTourByType,
    getTourByType,
    setTourToComplete,
    tourStatus,
    mode,
    startTourTilesEnabled,
    isAllowedToStartTour,
    initializeFloatingVideoContainer,
    allowedToursForMyRole,
  };
  return store;
});
